import { imagensUsadas } from '@/global/components/Markdown/markdown-tokens'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import { useCamapanhaOutletContext } from '@/pages/Campanhas.context'
import dayjs from 'dayjs'
import { nanoid } from 'nanoid'
import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    CampanhaImagemEnvio,
    campanhas,
    campanhaSchema,
    CreateCampanha,
    PublicarCampanha as PublicarCampanhaBody,
} from 'service/api'
import { toast } from 'sonner'
import { FormCampanha } from '../forms/FormCampanha'

const MIMES_ACEITOS = ['image/png', 'image/jpeg', 'image/webp']

const TAMANHO_MAXIMO_IMAGEM = 8 * 1024 * 1024

/** Base64 infla o payload em ~33%; o teto agregado evita um 413 no envio. */
const TAMANHO_MAXIMO_TOTAL = 24 * 1024 * 1024

/** Monta o corpo do POST a partir dos campos do formulário. */
function montarCorpo(
    dados: CreateCampanha,
    imagens: CampanhaImagemEnvio[],
): PublicarCampanhaBody {
    // Imagem anexada e depois apagada do corpo não vai no envio.
    const usadas = imagensUsadas(dados.corpoMarkdown, imagens)

    return {
        titulo: dados.titulo,
        mensagem: dados.mensagem,
        corpoMarkdown: dados.corpoMarkdown || undefined,
        modos: dados.modos,
        severity: dados.severity,
        alvo: { clienteIds: dados.clienteIds, categorias: dados.categorias },
        inicio: dados.umaVez ? undefined : dayjs(dados.inicio).toISOString(),
        fim: dados.umaVez ? undefined : dayjs(dados.fim).toISOString(),
        umaVez: dados.umaVez,
        botao:
            dados.botaoLabel && dados.botaoUrl
                ? { label: dados.botaoLabel, url: dados.botaoUrl }
                : undefined,
        imagens: usadas.length ? usadas : undefined,
    }
}

async function lerImagem(arquivo: File): Promise<CampanhaImagemEnvio> {
    const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(arquivo)
    })

    return { id: nanoid(), mime: arquivo.type, base64 }
}

export default function PublicarCampanha() {
    const [searchParams] = useSearchParams()
    const duplicarId = searchParams.get('duplicar')

    const { data: lista = [] } = useCamapanhaOutletContext()
    const modelo = lista.find(campanha => campanha.id === duplicarId)

    const [imagens, setImagens] = useState<CampanhaImagemEnvio[]>([])

    const imagensRef = useRef(imagens)

    const { mutateAsync, isLoading } = campanhas.mutations.useCreate({
        invalidateQuery: true,
        onSuccess: () => {
            toast.success('Campanha publicada')
        },
        onError: e => {
            toast.error(e.message)
        },
    })

    /**
     * Avalia o lote inteiro com acumulador local — arquivo a arquivo, todos
     * leriam o mesmo total de partida e o teto agregado não valeria nada.
     *
     * @returns O id de cada imagem aceita, usado no token do corpo.
     */
    async function anexarImagens(arquivos: File[]) {
        const aceitas: CampanhaImagemEnvio[] = []
        const atuais = imagensRef.current

        // 4 caracteres base64 viram 3 bytes.
        let bytes = atuais.reduce(
            (total, imagem) =>
                total + Math.floor((imagem.base64.length * 3) / 4),
            0,
        )

        for (const arquivo of arquivos) {
            if (arquivo.size > TAMANHO_MAXIMO_IMAGEM) {
                toast.error(`${arquivo.name}: imagem acima de 8 MB`)
                continue
            }

            if (!MIMES_ACEITOS.includes(arquivo.type)) {
                toast.error(`${arquivo.name}: formato não suportado`)
                continue
            }

            if (bytes + arquivo.size > TAMANHO_MAXIMO_TOTAL) {
                toast.error('As imagens da campanha somam mais de 24 MB')
                break
            }

            try {
                aceitas.push(await lerImagem(arquivo))
                bytes += arquivo.size
            } catch {
                toast.error(`${arquivo.name}: não foi possível ler a imagem`)
            }
        }

        if (!aceitas.length) return []

        imagensRef.current = [...atuais, ...aceitas]
        setImagens(imagensRef.current)

        return aceitas.map(imagem => imagem.id)
    }

    return (
        <Dialog.Root
            key={modelo?.id ?? 'nova-campanha'}
            open={hasPermission(['create', 'Notificações'])}
            loading={isLoading}
            schema={campanhaSchema}
            defaultValues={{
                titulo: modelo?.titulo ?? '',
                mensagem: modelo?.mensagem ?? '',
                corpoMarkdown: modelo?.corpoMarkdown ?? '',
                severity: modelo?.severity ?? 'info',
                inicio: dayjs().format('YYYY-MM-DDTHH:mm'),
                fim: dayjs().add(7, 'day').format('YYYY-MM-DDTHH:mm'),
                umaVez: modelo?.umaVez ?? false,
                modos: modelo?.modos ?? [],
                clienteIds: modelo?.alvo.clienteIds ?? [],
                categorias: modelo?.alvo.categorias ?? [],
                botaoLabel: modelo?.botao?.label ?? '',
                botaoUrl: modelo?.botao?.url ?? '',
                _modo_badge: modelo?.modos.includes('badge'),
                _modo_toast: modelo?.modos.includes('toast'),
                _modo_modal: modelo?.modos.includes('modal'),
            }}
            onSubmit={dados => mutateAsync(montarCorpo(dados, imagens))}
            width="3xl"
        >
            <Dialog.Title>Publicar notificação</Dialog.Title>
            <Dialog.Content>
                <FormCampanha imagens={imagens} anexarImagens={anexarImagens} />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
